import React, { useEffect, useState } from 'react'
import { FormApi } from 'final-form'
import { Form } from 'react-final-form'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import {
  FormPageProps,
  FormValuesProps,
  FormWizardProps,
} from '../interfaces/form'
import FormButton from '../components/FormButton'
import { useTheme } from '@mui/material'

const Wizard = ({
  onSubmit,
  initialValues,
  pages,
  setFormValues,
}: FormWizardProps) => {
  const theme = useTheme();
  //@ts-ignore
  const custom = theme?.custom;

  const params = {
    age: custom?.appConfig?.age,
    isSmoker: custom?.appConfig?.isSmoker,
    postcode: custom?.appConfig?.postcode,
    employment: custom?.appConfig?.employment,
    income: custom?.appConfig?.income,
    occupation: {
      code: custom?.appConfig?.occupationCode,
      description: custom?.appConfig?.occupationDescription,
    },
    isPartnerCovered:
      custom?.appConfig?.isPartnerCovered === 'Yes' ? 'true' : 'false',
    partnerAge: custom?.appConfig?.partnerAge,
    partnerEmployment: custom?.appConfig?.partnerEmployment,
    partnerIncome: custom?.appConfig?.partnerIncome,
    isPartnerSmoker:
      custom?.appConfig?.isPartnerSmoker === 'Yes' ? 'true' : 'false',
    partnerOccupation: {
      code: custom?.appConfig?.partnerOccupationCode,
      description: custom?.appConfig?.partnerOccupationDescription,
    },
  }
  const [page, setPage] = useState(0)
 
  const getInitialPage = (values = initialValues) => {
    let activePage = React.Children.toArray(pages)[page]
    let counterPage = page
    while (
      //@ts-ignore
      activePage?.props?.skip &&
      //@ts-ignore
      activePage?.props?.skip(values) &&
      counterPage < pages.length
    ) {
      counterPage = Math.min(counterPage + 1, pages.length - 1)
      activePage = React.Children.toArray(pages)[counterPage]
    }

    setFormValues({
      ...initialValues,
      currentPage: counterPage,
      totalPages: React.Children.toArray(pages).length,
    })
    setPage(counterPage)
  };

  const next = (values: FormValuesProps) => {
    let nextPage = Math.min(page + 1, pages.length - 1)
    let activePage = React.Children.toArray(pages)[nextPage]

    let counterPage = nextPage
    while (
      //@ts-ignore
      activePage?.props?.skip &&
      //@ts-ignore
      activePage?.props?.skip(values) &&
      counterPage < pages.length
    ) {
      counterPage = Math.min(counterPage + 1, pages.length - 1)
      activePage = React.Children.toArray(pages)[counterPage]
    }

    setFormValues({
      ...values,
      currentPage: counterPage,
      totalPages: React.Children.toArray(pages).length,
    })
    setPage(counterPage)
  }

  const previous = (values: FormValuesProps) => {
    let prevPage = Math.max(page - 1, 0)
    let activePage = React.Children.toArray(pages)[prevPage]

    let counterPage = prevPage
    while (
      //@ts-ignore
      activePage?.props?.skip &&
      //@ts-ignore
      activePage?.props?.skip(values) &&
      counterPage > 0
    ) {
      counterPage = Math.max(counterPage - 1, 0)
      activePage = React.Children.toArray(pages)[counterPage]
    }

    setFormValues({
      ...values,
      currentPage: counterPage,
      totalPages: React.Children.toArray(pages).length,
    })
    setPage(counterPage)
  }

  const validate = (values: FormValuesProps) => {
    const activePage = React.Children.toArray(pages)[page]

    //@ts-ignore
    return activePage?.props?.validate ? activePage.props.validate(values) : {}
  }

  const handleSubmit = (values: FormValuesProps, form: FormApi) => {
    const isLastPage = page === React.Children.count(pages) - 1

    if (isLastPage && validate(values)) {
      return onSubmit(values)
    } else {
      next(values)
    }
  }

  const hasQueryString = () => {
    return custom?.appConfig?.requestType
  }

  useEffect(() => {
    if (!hasQueryString()) {
      setFormValues({
        ...initialValues,
        currentPage: getInitialPage(),
        totalPages: React.Children.toArray(pages).length,
      })
    } else {
      const currentPage = React.Children.toArray(pages).length - 2
      setFormValues({
        ...initialValues,
        ...params,
        currentPage,
        totalPages: React.Children.toArray(pages).length,
      })
      setPage(currentPage)
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [])

  const activePage = React.Children.toArray(pages)[page]
  const isLastPage = page === React.Children.count(pages) - 1
  //@ts-ignore
  const showPrevButton = page > 0 && !activePage?.props?.hidePreviousButton
  //@ts-ignore
  const showNextButton = !activePage?.props.hideNextButton
  //@ts-ignore
  const showContinueButton = !isLastPage && showNextButton
  //@ts-ignore
  const showSubmitButton = isLastPage && showNextButton
  //@ts-ignore
  const hideButtons = activePage?.props?.hideButtons

  return (
    <Form
      initialValues={hasQueryString() ? params : initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      render={({ handleSubmit, values, submitting, pristine, valid }) => (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div style={{ minHeight: 450 }}>{activePage}</div>

          {!hideButtons && (
            <Container maxWidth="md" id="buttons">
              <Box
                sx={{ mt: 7, mb: 12, display: 'flex' }}
                flexDirection="column"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {showPrevButton && (
                      <FormButton
                        id="previous"
                        type="button"
                        onClick={() => previous(values)}
                        sx={{ fontWeight: 'bold' }}
                        disableRipple
                      >
                        Previous
                      </FormButton>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    justifyContent="flex-end"
                    sx={{ display: 'flex' }}
                  >
                    {showContinueButton && (
                      <FormButton
                        id="continue"
                        type="submit"
                        sx={{ fontWeight: 'bold' }}
                        disableRipple
                        disabled={!valid}
                      >
                        Continue
                      </FormButton>
                    )}
                    {showSubmitButton && (
                      <FormButton
                        id="submit"
                        type="submit"
                        sx={{ fontWeight: 'bold' }}
                        disableRipple
                        disabled={submitting || pristine || !valid}
                      >
                        Submit
                      </FormButton>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Container>
          )}
        </form>
      )}
    />
  )
}

Wizard.Page = function ({ children }: FormPageProps) {
  return <>{children}</>
}

export default Wizard
